import {
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  getAuth
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthContextType } from '@/types';
import api from '../services/api';

// Firebaseの設定
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "your-api-key",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "your-project-id.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "your-project-id"
};

// Firebaseアプリの初期化
const app = initializeApp(firebaseConfig);
// 認証オブジェクトの初期化
const auth = getAuth(app);

// localStorageのキー（選択中の店舗を永続化）
const SELECTED_STORE_KEY = 'recrew_selected_store_id';

// 以降は AuthContext の実装
// デフォルト値を持つコンテキストを作成
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  storeId: "",
  storeIds: [],
  storeName: "",
  isAdmin: false,
  checkAdmin: async () => false,
  switchStore: () => {},
  refreshStoreName: async () => {},
  signOut: async () => {}
});

// コンテキストを使用するためのカスタムフック
export const useAuth = () => useContext(AuthContext);

// カスタムクレームからstoreIds配列を取得（旧storeId単体との後方互換）
function extractStoreIds(claims: Record<string, unknown>): string[] {
  const storeIds: string[] = [];

  // 新形式: storeIds配列
  const claimStoreIds = claims.storeIds;
  if (Array.isArray(claimStoreIds)) {
    for (const id of claimStoreIds) {
      if (typeof id === 'string' && id) {
        storeIds.push(id);
      }
    }
  }

  // 旧形式: storeId単体（後方互換）
  const oldStoreId = claims.storeId;
  if (typeof oldStoreId === 'string' && oldStoreId && !storeIds.includes(oldStoreId)) {
    storeIds.push(oldStoreId);
  }

  return storeIds;
}

// 認証プロバイダーコンポーネント
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [storeId, setStoreId] = useState<string>("");
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [storeName, setStoreName] = useState<string>("");
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // 店舗名を取得
  const fetchStoreName = useCallback(async (targetStoreId: string) => {
    if (!targetStoreId) {
      setStoreName("");
      return;
    }
    try {
      const storeInfo = await api.store.getStoreInfo(targetStoreId);
      setStoreName(storeInfo.name || "");
    } catch {
      setStoreName("");
    }
  }, []);

  const resolveAdminByEmail = async (targetStoreId: string, adminEmail?: string): Promise<boolean> => {
    if (!adminEmail) {
      setIsAdmin(false);
      return false;
    }

    try {
      const adminFlag = await api.store.checkAdmin(targetStoreId, adminEmail);
      setIsAdmin(adminFlag);
      return adminFlag;
    } catch (error) {
      setIsAdmin(false);
      return false;
    }
  };

  // ログアウト関数
  const signOut = async () => {
    try {
      localStorage.removeItem(SELECTED_STORE_KEY);
      await firebaseSignOut(auth);
    } catch (error) {
    }
  };

    // 運営店舗チェック関数
  const checkAdmin = async (storeId: string): Promise<boolean> => {
    return resolveAdminByEmail(storeId, currentUser?.email || undefined);
  };

  // カスタムクレームからstoreId等を抽出する共通関数
  const extractClaimsFromToken = useCallback(async (user: User) => {
    const idTokenResult = await user.getIdTokenResult(true);
    const claims = idTokenResult.claims;

    // storeId
    const claimStoreId = claims.storeId;
    if (typeof claimStoreId === 'string' && !!claimStoreId) {
      setStoreId(claimStoreId);
      resolveAdminByEmail(claimStoreId, user.email || undefined);
    } else {
      setStoreId("");
      setIsAdmin(false);
    }
  }, []);
  // 店舗切り替え
  const switchStore = useCallback((newStoreId: string) => {
    if (!storeIds.includes(newStoreId)) return;
    setStoreId(newStoreId);
    localStorage.setItem(SELECTED_STORE_KEY, newStoreId);
    fetchStoreName(newStoreId);
    // admin権限も切り替え先の店舗で再チェック
    if (currentUser?.email) {
      resolveAdminByEmail(newStoreId, currentUser.email);
    }
  }, [storeIds, currentUser, fetchStoreName]);

  useEffect(() => {
    // ユーザーの認証状態を監視
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          await extractClaimsFromToken(user);
          // forceRefresh=true で最新のカスタムクレームを取得（キャッシュを無視）
          const idTokenResult = await user.getIdTokenResult(true);
          const ids = extractStoreIds(idTokenResult.claims as Record<string, unknown>);
          setStoreIds(ids);

          if (ids.length > 0) {
            // localStorageに保存された選択をまず確認
            const savedStoreId = localStorage.getItem(SELECTED_STORE_KEY);
            const activeStoreId = (savedStoreId && ids.includes(savedStoreId))
              ? savedStoreId
              : ids[0];

            setStoreId(activeStoreId);
            localStorage.setItem(SELECTED_STORE_KEY, activeStoreId);
            fetchStoreName(activeStoreId);
            resolveAdminByEmail(activeStoreId, user.email || undefined);
          } else {
            setIsAdmin(false);
          }
        } catch {
          setIsAdmin(false);
        }
      } else {
        setStoreId("");
        setStoreIds([]);
        setStoreName("");
        setIsAdmin(false);
      }
      // クレーム取得完了後にローディングを解除（競合状態を防ぐ）
      setLoading(false);
    });

    // クリーンアップ関数でリスナーを解除
    return unsubscribe;
  }, [extractClaimsFromToken, fetchStoreName]);

  // 現在選択中の店舗名を再取得する
  const refreshStoreName = useCallback(async () => {
    if (storeId) await fetchStoreName(storeId);
  }, [storeId, fetchStoreName]);

  // コンテキストの値
  const value = {
    currentUser,
    loading,
    storeId,
    storeIds,
    storeName,
    isAdmin,
    checkAdmin,
    switchStore,
    refreshStoreName,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
